import { CHUNK_SIZE, TileType, RodentType, EXPLORATION_HOUSE_CENTER_X,
    UNDERGROUND_ROOM_X1, UNDERGROUND_ROOM_X2, UNDERGROUND_ROOM_Y1, UNDERGROUND_ROOM_Y2,
    UNDERGROUND_LADDER_BOTTOM, SPAWN_CENTER_X, TREE_TRUNK_HEIGHT, TREE_LEAF_LAYERS,
    isNestSite, isOreSeed, getKiwenTileForBiome, isShrubSeed, getKasiTileForBiome } from './constants';
import { getZoneAtTile, getZoneMainBlock, CosmologyZone } from './CosmologyMap';
import { isTunnelArea } from './TunnelMap';
import { getBiomeAtTile } from './BiomeMap';
import { getOreSeptomino, SEPTOMINO_MAX_EXTENT } from './Septominoes';

/** Spacing between natural trees (in tiles). */
const NATURAL_TREE_SPACING = 10;

/**
 * Check if (worldX, worldY) falls within an ore vein septomino.
 * Searches nearby seed points (within SEPTOMINO_MAX_EXTENT) and checks
 * if this position is one of the 7 tiles in that vein's shape.
 * All 7 cells must pass the wouldBeStone check or the entire vein is skipped,
 * preventing lone ore blocks from partial veins cut by tunnels or zone boundaries.
 * Returns the biome's KIWEN tile type, or null if not part of any vein.
 */
function getOreVeinTile(
    worldX: number, worldY: number, seed: number,
    wouldBeStone: (x: number, y: number) => boolean
): TileType | null {
    const r = SEPTOMINO_MAX_EXTENT;
    for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
            const sx = worldX - dx;
            const sy = worldY - dy;
            if (!isOreSeed(sx, sy)) continue;

            // Skip seeds near the underground room — their shapes would
            // be fragmented by the designed room layout
            if (sx >= UNDERGROUND_ROOM_X1 - r && sx <= UNDERGROUND_ROOM_X2 + r
                && sy >= UNDERGROUND_ROOM_Y1 - r && sy <= UNDERGROUND_ROOM_Y2 + r) {
                continue;
            }

            // Skip seeds near the spawn platform — decorative blocks override ore
            if (sx >= SPAWN_CENTER_X - 5 - r && sx <= SPAWN_CENTER_X + 5 + r
                && sy >= -15 && sy <= 15) {
                continue;
            }

            const shape = getOreSeptomino(seed, sx, sy);
            for (const cell of shape) {
                if (cell[0] === dx && cell[1] === dy) {
                    const biomeWord = getBiomeAtTile(sx, sy);
                    if (biomeWord < 0) return null;
                    // All 7 cells must land in stone — skip vein if any cell
                    // would be carved by a tunnel, overridden by a zone, or
                    // fall in the dirt/grass/sky layer
                    for (const c of shape) {
                        if (!wouldBeStone(sx + c[0], sy + c[1])) return null;
                    }
                    return getKiwenTileForBiome(biomeWord);
                }
            }
        }
    }
    return null;
}

/**
 * Check if (worldX, worldY) is part of a biome shrub cluster.
 * Shrub clusters are 3-wide rows of kasi tiles sitting 1 tile above grass.
 * The center tile is the seed point (determined by isShrubSeed). The seed
 * must have: down-left, down, and down-right all grass; left, center, and
 * right all sky (no trees). If valid, all 3 tiles become the biome's kasi tile.
 * Returns the kasi tile type, or null if not part of a shrub cluster.
 */
function getShrubClusterTile(
    worldX: number, worldY: number, seed: number,
    calculateSurfaceYFn: (worldX: number, seed: number) => number,
    getValleyWaterLevelFn: (worldX: number) => number | null,
    spawnCenter: number, ehX1: number, ehX2: number,
): TileType | null {
    // Check if this tile or a neighbor is the center of a shrub cluster.
    // Clusters are 3 tiles wide, so check offsets -1, 0, +1 from worldX.
    for (let dx = -1; dx <= 1; dx++) {
        const cx = worldX - dx; // candidate center X
        const cy = worldY + 1;  // candidate center Y (one below this tile, i.e. the grass row)

        if (!isShrubSeed(cx, cy)) continue;

        // Center must be exactly 1 above the surface (surfaceY)
        const surfCenter = calculateSurfaceYFn(cx, seed);
        if (cy !== surfCenter) continue;

        // Down-left, down, down-right must all be grass (surface level)
        const surfLeft = calculateSurfaceYFn(cx - 1, seed);
        const surfRight = calculateSurfaceYFn(cx + 1, seed);
        if (surfLeft !== surfCenter || surfRight !== surfCenter) continue;

        // All 3 positions must be free of trees (prevents partial clusters)
        let treeConflict = false;
        for (let tx = cx - 1; tx <= cx + 1; tx++) {
            if (getTreeTileAt(tx, worldY, seed, calculateSurfaceYFn,
                getValleyWaterLevelFn, spawnCenter, ehX1, ehX2) !== null) {
                treeConflict = true;
                break;
            }
        }
        if (treeConflict) continue;

        // The biome's kasi tile
        const biomeWord = getBiomeAtTile(cx, cy);
        if (biomeWord < 0) continue;
        const kasiTile = getKasiTileForBiome(biomeWord);
        if (kasiTile === null) continue;

        return kasiTile;
    }
    return null;
}

/**
 * Check if a natural tree should place a block at (worldX, worldY).
 * Trees are placed every NATURAL_TREE_SPACING tiles on grass surface biomes,
 * avoiding spawn platform, exploration house, and non-grass zones.
 */
function getTreeTileAt(
    worldX: number, worldY: number, seed: number,
    calculateSurfaceYFn: (worldX: number, seed: number) => number,
    getValleyWaterLevelFn: (worldX: number) => number | null,
    spawnCenter: number, ehX1: number, ehX2: number
): TileType | null {
    // Check each potential tree center within leaf range (±3)
    for (let cx = worldX - 3; cx <= worldX + 3; cx++) {
        // Trees at fixed intervals
        if (((cx % NATURAL_TREE_SPACING) + NATURAL_TREE_SPACING) % NATURAL_TREE_SPACING !== 0) continue;

        // Seed-based thinning: ~1/3 of tree positions are skipped
        const treeHash = ((cx * 2654435761 + seed * 2246822519) >>> 0) % 3;
        if (treeHash === 0) continue;

        // Skip near spawn platform (with leaf padding)
        if (cx >= spawnCenter - 9 && cx <= spawnCenter + 9) continue;

        // Skip near exploration house (with leaf padding)
        if (cx >= ehX1 - 4 && cx <= ehX2 + 4) continue;

        // Must be on grass surface zone, with 3-tile buffer from Yggdrasil/dungeon towers.
        // Leaves extend ±3 from center; checking ±6 ensures a 3-tile gap beyond leaf edges.
        const surfaceY = calculateSurfaceYFn(cx, seed);
        if (getZoneAtTile(cx - 6, surfaceY) !== CosmologyZone.GRASS_SURFACE) continue;
        if (getZoneAtTile(cx + 6, surfaceY) !== CosmologyZone.GRASS_SURFACE) continue;

        // Skip underwater areas
        const waterLevel = getValleyWaterLevelFn(cx);
        if (waterLevel !== null) continue;

        const baseY = surfaceY - 1; // trunk bottom (one tile above grass)
        const relX = worldX - cx;

        // Trunk: single column at tree center
        if (relX === 0 && worldY <= baseY && worldY > baseY - TREE_TRUNK_HEIGHT) {
            return TileType.TRUNK;
        }

        // Leaves: pyramidal canopy
        let leafY = baseY - TREE_TRUNK_HEIGHT;
        for (const layer of TREE_LEAF_LAYERS) {
            for (let r = 0; r < layer.rows; r++) {
                if (worldY === leafY && Math.abs(relX) <= layer.halfWidth) {
                    return TileType.LEAVES;
                }
                leafY--;
            }
        }
    }
    return null;
}

export interface TerrainGeneratorContext {
    seed: number;
    setRuneHieroglyph(x: number, y: number, index: number): void;
    hasNestData(x: number, y: number): boolean;
    addNestOccupant(x: number, y: number, occupant: {
        rodentType: string;
        timer: number;
        facing: number;
    }): void;
}

/** Returns tile type for a position in the underground room (cavern-local 1-based coords). */
function getUndergroundRoomTile(x: number, y: number): TileType {
    let tile = TileType.STONE;

    // SKY carve-outs
    if (y > 3 && y < 7 && x > 2 && x < 8) tile = TileType.SKY;           // rect1
    if (y > 1 && y < 7 && x > 11 && x < 32) tile = TileType.SKY;         // rect2
    if (y === 6 && x > 7 && x < 12) tile = TileType.SKY;                  // rect3
    if (y > 6 && y < 9 && x > 18 && x < 24) tile = TileType.SKY;         // rect4
    if (y > 1 && y < 4 && x > 31 && x < 41) tile = TileType.SKY;         // rect5
    if (y > 6 && y < 11 && x > 28 && x < 32) tile = TileType.SKY;        // rect6
    if (y > 10 && y < 20 && x > 18 && x < 24) tile = TileType.SKY;       // rect7
    if (y > 10 && y < 13 && x === 24) tile = TileType.SKY;                  // rect8
    if (y > 10 && y < 20 && x > 24 && x < 32) tile = TileType.SKY;       // rect9
    if (y > 16 && y < 20 && x > 32 && x < 36) tile = TileType.SKY;       // rect10
    if (y > 10 && y < 26 && x > 35 && x < 47) tile = TileType.SKY;       // rect11
    if (y > 10 && y < 14 && x > 46 && x < 49) tile = TileType.SKY;       // rect12

    // LADDER columns
    if (y > 6 && y < 13 && x === 29) tile = TileType.LADDER;              // rect13
    if (y > 19 && y < 23 && x === 36) tile = TileType.LADDER;             // rect14

    // STONE platforms (override SKY)
    if (y === 20 && x > 36 && x < 40) tile = TileType.STONE;              // rect15
    if (y === 17 && x > 41 && x < 45) tile = TileType.STONE;              // rect16

    // Yellow door columns (override SKY) — 2-tall solid doors, unlocked by yellow key
    if (y > 17 && y < 20 && x === 24) tile = TileType.YELLOW_DOOR;        // rect17
    if (y > 17 && y < 20 && x === 32) tile = TileType.YELLOW_DOOR;        // rect18

    // Yellow key pickup (rel x=28, y=19)
    if (y === 19 && x === 28) tile = TileType.YELLOW_KEY;

    // Single DIRT blocks
    if (y === 8 && x === 20) tile = TileType.DIRT;
    if (y === 19 && x === 19) tile = TileType.DIRT;
    if (y === 19 && x === 20) tile = TileType.DIRT;


    return tile;
}

/** Move-only extraction of WorldData.generateChunk(). */
export function generateChunkData(
    ctx: TerrainGeneratorContext,
    cx: number,
    cy: number,
    calculateSurfaceYFn: (worldX: number, seed: number) => number,
    calculatePortalSurfaceYFn: (seed: number) => number,
    getValleyWaterLevelFn: (worldX: number) => number | null,
): number[][] {
        const chunk: number[][] = [];

        const sc = SPAWN_CENTER_X;

        // Pre-calculate portal surface Y for spawn center
        // Uses max surfaceY across sc-5..sc+5 so portal doesn't float above nearby terrain
        const portalSurfaceY = calculatePortalSurfaceYFn(ctx.seed);

        // Pre-calculate exploration house surface Y and bounds
        const ehX1 = EXPLORATION_HOUSE_CENTER_X - 5;
        const ehX2 = EXPLORATION_HOUSE_CENTER_X + 5;
        let ehMinY = calculateSurfaceYFn(EXPLORATION_HOUSE_CENTER_X, ctx.seed);
        for (let dx = -5; dx <= 5; dx++) {
            const ey = calculateSurfaceYFn(EXPLORATION_HOUSE_CENTER_X + dx, ctx.seed);
            if (ey < ehMinY) ehMinY = ey;
        }
        const ehSurfaceY = ehMinY - 1;

        // Callback for ore vein viability: returns true if (x,y) would be stone
        // (not in a tunnel, not in a zone override, not above the stone layer)
        const wouldBeStone = (x: number, y: number): boolean => {
            const sy = calculateSurfaceYFn(x, ctx.seed);
            if (y < sy + 4) return false;
            const zone = getZoneAtTile(x, y);
            if (getZoneMainBlock(zone) !== null) return false;
            if (isTunnelArea(x, y, ctx.seed)) return false;
            return true;
        };

        for (let y = 0; y < CHUNK_SIZE; y++) {
            const row: number[] = [];
            for (let x = 0; x < CHUNK_SIZE; x++) {
                const worldY = cy * CHUNK_SIZE + y;
                const worldX = cx * CHUNK_SIZE + x;

                const surfaceY = calculateSurfaceYFn(worldX, ctx.seed);

                // Ladder at sc+4: from spawn house roof down into underground room
                if (worldX === sc + 4 && worldY >= portalSurfaceY - 7 && worldY <= UNDERGROUND_LADDER_BOTTOM) {
                    row.push(TileType.LADDER);
                    continue;
                }

                // Underground room: designed cavern layout
                if (worldX >= UNDERGROUND_ROOM_X1 && worldX <= UNDERGROUND_ROOM_X2
                    && worldY >= UNDERGROUND_ROOM_Y1 && worldY <= UNDERGROUND_ROOM_Y2) {
                    const lx = worldX - UNDERGROUND_ROOM_X1 + 1;
                    const ly = worldY - UNDERGROUND_ROOM_Y1 + 1;
                    row.push(getUndergroundRoomTile(lx, ly));
                    continue;
                }

                // Spawn platform zone: sc-5..sc+5 uses portalSurfaceY as effective surface
                // with an 11-wide alternating turquoise/amethyst platform, portal frame at sc+1,
                // opal+trunk pillars at sc±5, and an opal roof connecting them
                if (worldX >= sc - 5 && worldX <= sc + 5) {
                    const dx = worldX - sc; // relative to spawn center
                    if (dx === 1 && worldY === portalSurfaceY - 7) {
                        // Rune at roof level
                        row.push(TileType.RUNE);
                        ctx.setRuneHieroglyph(worldX, worldY, 0);
                    } else if (dx === 1 && (worldY === portalSurfaceY - 8 || worldY === portalSurfaceY - 9)) {
                        // Portal above rune
                        row.push(TileType.PORTAL);
                    } else if (worldY === portalSurfaceY - 7) {
                        // Alternating roof matching the floor pattern
                        const dist = Math.abs(dx);
                        row.push(dist % 2 === 0 ? TileType.TURQUOISE : TileType.AMETHYST);
                    } else if (worldY === portalSurfaceY - 14 && dx >= -1 && dx <= 5) {
                        // Upper alternating roof connecting upper columns
                        const dist = Math.abs(dx);
                        row.push(dist % 2 === 0 ? TileType.TURQUOISE : TileType.AMETHYST);
                    } else if ((dx === -1 || dx === 5) && worldY >= portalSurfaceY - 13 && worldY <= portalSurfaceY - 8) {
                        // Upper opal columns on top of lower roof
                        row.push(TileType.OPAL);
                    } else if ((dx === -5 || dx === 5) && worldY >= portalSurfaceY - 6 && worldY <= portalSurfaceY - 3) {
                        // Pillar: 4 opal blocks (portalSurfaceY-6 through -3)
                        row.push(TileType.OPAL);
                    } else if ((dx === -5 || dx === 5) && (worldY === portalSurfaceY - 1 || worldY === portalSurfaceY - 2)) {
                        // Pillar: 2 trunk blocks at base (portalSurfaceY-2 and -1)
                        row.push(TileType.TRUNK);
                    } else if (worldY === portalSurfaceY) {
                        // Alternating pattern: even distance = turquoise, odd = amethyst
                        const dist = Math.abs(dx);
                        row.push(dist % 2 === 0 ? TileType.TURQUOISE : TileType.AMETHYST);
                    } else if (worldY < portalSurfaceY) {
                        row.push(TileType.SKY);
                    } else {
                        // Below platform: use natural terrain generation
                        if ((dx === -5 || dx === 5) && worldY < surfaceY + 4) {
                            // Continue alternating tile pattern down the edge columns
                            const dist = Math.abs(dx);
                            const dy = worldY - portalSurfaceY;
                            row.push((dist + dy) % 2 === 0 ? TileType.TURQUOISE : TileType.AMETHYST);
                        } else if (dx >= -4 && dx <= 4 && worldY < surfaceY + 4) {
                            // Fill interior below platform with stone
                            row.push(TileType.STONE);
                        } else {
                            // Deep underground: normal terrain with cosmology zones, tunnels, ore
                            const zoneBlock = getZoneMainBlock(getZoneAtTile(worldX, worldY));
                            let tile: TileType;
                            if (zoneBlock !== null) {
                                tile = zoneBlock;
                            } else {
                                const oreTile = getOreVeinTile(worldX, worldY, ctx.seed, wouldBeStone);
                                tile = oreTile !== null ? oreTile : TileType.STONE;
                            }
                            if (tile !== TileType.SKY
                                && tile !== TileType.TRUNK && tile !== TileType.LEAVES
                                && isTunnelArea(worldX, worldY, ctx.seed)) {
                                tile = TileType.SKY;
                            }
                            row.push(tile);
                        }
                    }
                } else if (worldX >= ehX1 && worldX <= ehX2) {
                    // Exploration house zone: 11-wide RED_STONE building
                    const isEdge = worldX === ehX1 || worldX === ehX2;

                    if (isEdge && (worldY === ehSurfaceY - 1 || worldY === ehSurfaceY - 2)) {
                        // 2 TRUNK entry columns at each side (passable doorway)
                        row.push(TileType.TRUNK);
                    } else if (isEdge && worldY >= ehSurfaceY - 6 && worldY <= ehSurfaceY - 3) {
                        // RED_STONE wall above entry columns
                        row.push(TileType.RED_STONE);
                    } else if (worldY === ehSurfaceY - 7) {
                        // RED_STONE roof
                        row.push(TileType.RED_STONE);
                    } else if (worldY === ehSurfaceY) {
                        // RED_STONE platform
                        row.push(TileType.RED_STONE);
                    } else if (worldY < ehSurfaceY) {
                        // Air above platform (interior)
                        row.push(TileType.SKY);
                    } else {
                        // Below platform: foundation above surface, then normal terrain
                        if (worldY < surfaceY) {
                            // Above natural surface: foundation fill
                            row.push(isEdge ? TileType.RED_STONE : TileType.STONE);
                        } else {
                            // At or below natural surface: normal terrain with cosmology zones + tunnels + ore
                            const zoneBlock = getZoneMainBlock(getZoneAtTile(worldX, worldY));
                            let tile: TileType;
                            if (zoneBlock !== null) {
                                tile = zoneBlock;
                            } else if (worldY === surfaceY) {
                                tile = TileType.GRASS;
                            } else if (worldY < surfaceY + 4) {
                                tile = TileType.DIRT;
                            } else {
                                const oreTile = getOreVeinTile(worldX, worldY, ctx.seed, wouldBeStone);
                                tile = oreTile !== null ? oreTile : TileType.STONE;
                            }
                            if (tile !== TileType.SKY
                                && tile !== TileType.TRUNK && tile !== TileType.LEAVES
                                && isTunnelArea(worldX, worldY, ctx.seed)) {
                                tile = TileType.SKY;
                            }
                            row.push(tile);
                        }
                    }
                } else {
                    // Normal terrain generation

                    // Cosmology zone fill: each zone has a main block (or null for normal terrain)
                    const zoneBlock = getZoneMainBlock(getZoneAtTile(worldX, worldY));

                    // Standard terrain — flat water level per valley
                    const waterLevel = getValleyWaterLevelFn(worldX);
                    const isUnderwater = waterLevel !== null && waterLevel < surfaceY;
                    let tile: TileType;
                    if (zoneBlock !== null) {
                        tile = zoneBlock;
                    } else if (worldY < surfaceY) {
                        tile = TileType.SKY;
                    } else if (worldY === surfaceY) {
                        // Grass becomes dirt when submerged below water line
                        if (isUnderwater) {
                            tile = TileType.DIRT;
                        } else if (isNestSite(worldX, worldY)) {
                            // Rats nest on terrain edges where air is exposed to the side
                            const leftSurfaceY = calculateSurfaceYFn(worldX - 1, ctx.seed);
                            const rightSurfaceY = calculateSurfaceYFn(worldX + 1, ctx.seed);
                            tile = (leftSurfaceY > surfaceY || rightSurfaceY > surfaceY)
                                ? TileType.RATS_NEST : TileType.GRASS;
                        } else {
                            tile = TileType.GRASS;
                        }
                    } else if (worldY < surfaceY + 4) {
                        tile = TileType.DIRT;
                    } else {
                        // Stone — check for ore vein placement
                        const oreTile = getOreVeinTile(worldX, worldY, ctx.seed, wouldBeStone);
                        tile = oreTile !== null ? oreTile : TileType.STONE;
                    }

                    // Valley water: fill SKY tiles from flat water level down to valley floor.
                    // Topmost water (worldY === waterLevel) is WATER_SURFACE — looks like water
                    // but is passable like SKY so players fall through the surface.
                    if (tile === TileType.SKY && isUnderwater && worldY >= waterLevel!) {
                        tile = worldY === waterLevel ? TileType.WATER_SURFACE : TileType.WATER;
                    }

                    // Natural trees on grass surface (every NATURAL_TREE_SPACING tiles)
                    if (tile === TileType.SKY) {
                        const treeTile = getTreeTileAt(worldX, worldY, ctx.seed,
                            calculateSurfaceYFn, getValleyWaterLevelFn, sc, ehX1, ehX2);
                        if (treeTile !== null) {
                            tile = treeTile;
                        }
                    }

                    // Biome shrub clusters: 3-wide kasi rows sitting on flat grass
                    if (tile === TileType.SKY) {
                        const shrubTile = getShrubClusterTile(worldX, worldY, ctx.seed,
                            calculateSurfaceYFn, getValleyWaterLevelFn, sc, ehX1, ehX2);
                        if (shrubTile !== null) {
                            tile = shrubTile;
                        }
                    }

                    // Edge access columns just outside the spawn platform (sc±6)
                    if (worldX === sc - 6 || worldX === sc + 6) {
                        const natAtPlatform = portalSurfaceY < surfaceY ? TileType.SKY
                            : portalSurfaceY === surfaceY ? TileType.GRASS
                            : portalSurfaceY < surfaceY + 4 ? TileType.DIRT
                            : TileType.STONE;
                        const natBelow = (portalSurfaceY + 1) < surfaceY ? TileType.SKY
                            : (portalSurfaceY + 1) === surfaceY ? TileType.GRASS
                            : (portalSurfaceY + 1) < surfaceY + 4 ? TileType.DIRT
                            : TileType.STONE;

                        if (natAtPlatform === TileType.SKY && natBelow === TileType.SKY) {
                            // Ladder column from platform level down to ground (max 12)
                            if (worldY >= portalSurfaceY && worldY < surfaceY && worldY < portalSurfaceY + 12) {
                                tile = TileType.LADDER;
                            }
                        } else if (natAtPlatform === TileType.DIRT) {
                            // Dirt at platform level -> grass, 3 blocks above -> air
                            if (worldY === portalSurfaceY) {
                                tile = TileType.GRASS;
                            } else if (worldY >= portalSurfaceY - 3 && worldY < portalSurfaceY) {
                                tile = TileType.SKY;
                            }
                        }
                    }

                    // Edge access columns just outside the exploration house (ehX1-1, ehX2+1)
                    if (worldX === ehX1 - 1 || worldX === ehX2 + 1) {
                        const natAtPlatform = ehSurfaceY < surfaceY ? TileType.SKY
                            : ehSurfaceY === surfaceY ? TileType.GRASS
                            : ehSurfaceY < surfaceY + 4 ? TileType.DIRT
                            : TileType.STONE;
                        const natBelow = (ehSurfaceY + 1) < surfaceY ? TileType.SKY
                            : (ehSurfaceY + 1) === surfaceY ? TileType.GRASS
                            : (ehSurfaceY + 1) < surfaceY + 4 ? TileType.DIRT
                            : TileType.STONE;

                        if (natAtPlatform === TileType.SKY && natBelow === TileType.SKY) {
                            if (worldY >= ehSurfaceY && worldY < surfaceY && worldY < ehSurfaceY + 12) {
                                tile = TileType.LADDER;
                            }
                        } else if (natAtPlatform === TileType.DIRT) {
                            if (worldY === ehSurfaceY) {
                                tile = TileType.GRASS;
                            } else if (worldY >= ehSurfaceY - 3 && worldY < ehSurfaceY) {
                                tile = TileType.SKY;
                            }
                        }
                    }

                    // Tunnel network: carve SKY through all worldgen blocks except trees
                    // and the foundation blocks (grass + dirt) directly below natural tree trunks.
                    // Ore veins are safe: wouldBeStone rejects cells in tunnel areas.
                    if (tile !== TileType.SKY
                        && tile !== TileType.TRUNK && tile !== TileType.LEAVES
                        && isTunnelArea(worldX, worldY, ctx.seed)) {
                        let isTreeFoundation = false;
                        if (worldY >= surfaceY && worldY < surfaceY + 4) {
                            // Check if this column has a natural tree trunk directly above
                            const trunkCheck = getTreeTileAt(worldX, surfaceY - 1, ctx.seed,
                                calculateSurfaceYFn, getValleyWaterLevelFn, sc, ehX1, ehX2);
                            if (trunkCheck === TileType.TRUNK) {
                                isTreeFoundation = true;
                            }
                        }
                        if (!isTreeFoundation) {
                            tile = TileType.SKY;
                        }
                    }

                    row.push(tile);
                }
            }
            chunk.push(row);
        }
        return chunk;
        }
